import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Loader2,
  Search,
  X,
} from 'lucide-react';

import { useFirms } from '../../hooks/api';
import { useDebouncedValue } from '../../hooks/api/use-debounced-value';
import { cn } from '../../lib/utils';
import { Firm, FirmPlan, ListFirmsQueryParams } from '../../types/firm';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { FirmDetailDrawer } from './firm-detail-drawer';

export function FirmList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>(null);
  
  // Initialize filters from URL params only on mount
  const [filters, setFilters] = useState<{
    name: string;
    plan: FirmPlan | '';
    page: number;
    limit: number;
  }>(() => {
    const name = searchParams.get('name') || '';
    const plan = (searchParams.get('plan') as FirmPlan | null) || '';
    const page = parseInt(searchParams.get('page') || '1', 10);
    const limit = parseInt(searchParams.get('limit') || '20', 10);
    
    return {
      name,
      plan,
      page: isNaN(page) || page < 1 ? 1 : page,
      limit: isNaN(limit) || limit < 1 ? 20 : limit,
    };
  });

  // Debounce the name filter to avoid too many API calls
  const debouncedName = useDebouncedValue(filters.name, 500);

  // Get firmId from URL
  const firmIdFromUrl = searchParams.get('firmId');

  // Update URL params when filters change (debounced for name) - without causing re-render
  // Preserve firmId if it exists
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (debouncedName) {
      params.set('name', debouncedName);
    }
    
    if (filters.plan) {
      params.set('plan', filters.plan);
    }
    
    if (filters.page > 1) {
      params.set('page', filters.page.toString());
    }
    
    if (filters.limit !== 20) {
      params.set('limit', filters.limit.toString());
    }
    
    // Preserve firmId if it exists in URL
    if (firmIdFromUrl) {
      params.set('firmId', firmIdFromUrl);
    }
    
    // Only update if params actually changed
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    
    if (currentParams !== newParams) {
      // Use window.history.replaceState to update URL without triggering React Router navigation
      // This prevents full page re-renders while still updating the URL
      const newUrl = `${window.location.pathname}${newParams ? `?${newParams}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [debouncedName, filters.plan, filters.page, filters.limit, firmIdFromUrl, searchParams]);

  // Build query filters with proper types - convert empty string to undefined
  const queryFilters: ListFirmsQueryParams = {
    page: filters.page,
    limit: filters.limit,
    ...(debouncedName && { name: debouncedName }),
    ...(filters.plan && { plan: filters.plan as FirmPlan }),
  };

  // If firmId is in URL but not in current filters, fetch that specific firm
  const shouldFetchFirmById = firmIdFromUrl && !queryFilters.id;
  const firmByIdFilters: ListFirmsQueryParams | undefined = shouldFetchFirmById
    ? { id: parseInt(firmIdFromUrl, 10) }
    : undefined;

  const { data: firmsData, isLoading, error, refetch } = useFirms(queryFilters);
  const { data: firmByIdData } = useFirms(firmByIdFilters);

  // Extract data from the response
  const firms = useMemo(() => firmsData?.firms || [], [firmsData?.firms]);
  const firmById = firmByIdData?.firms?.[0] || null;
  const currentPage = firmsData?.page || 1;
  const totalPages = firmsData?.totalPages || 1;
  const total = firmsData?.total || 0;
  const pageSize = firmsData?.limit || 20;

  // Sync selectedFirm with URL parameter
  useEffect(() => {
    if (firmIdFromUrl) {
      const firmId = parseInt(firmIdFromUrl, 10);
      if (!isNaN(firmId)) {
        // First, try to find the firm in the current list
        const firmInList = firms.find((f) => f.id === firmId);
        if (firmInList) {
          setSelectedFirm(firmInList);
        } else if (firmById && firmById.id === firmId) {
          // If not in list, use the firm fetched by ID
          setSelectedFirm(firmById);
        }
      }
    } else {
      // If no firmId in URL, close drawer
      setSelectedFirm(null);
    }
  }, [firmIdFromUrl, firms, firmById]);

  const handleFirmClick = (firm: Firm) => {
    setSelectedFirm(firm);
    // Update URL with firmId
    const params = new URLSearchParams(searchParams);
    params.set('firmId', firm.id.toString());
    setSearchParams(params, { replace: true });
  };

  const handleCloseDrawer = () => {
    setSelectedFirm(null);
    // Remove firmId from URL
    const params = new URLSearchParams(searchParams);
    params.delete('firmId');
    setSearchParams(params, { replace: true });
  };

  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page !== undefined ? newFilters.page : 1, // Reset to page 1 when changing filters
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      name: '',
      plan: '',
      page: 1,
      limit: 20,
    });
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  // Smart pagination: show first, last, current, and nearby pages
  const paginationPages = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 7; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  const planOptions = [
    { value: '', label: 'All Plans' },
    { value: 'free', label: 'Free' },
    { value: 'full', label: 'Full' },
  ];

  const getPlanBadgeVariant = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'secondary';
      case 'full':
        return 'success';
      case 'custom':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-red-600">{error.message}</p>
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-stroke-primary bg-background p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary">Firms</h1>
            <p className="text-text-secondary">Manage and view firm information</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(showFilters && 'bg-accent')}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 h-4 w-4 text-text-secondary" />
          <Input
            placeholder="Search firms by name..."
            value={filters.name}
            onChange={(e) => {
              updateFilters({ name: e.target.value });
            }}
            className="pl-10"
          />
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 gap-4 rounded-lg bg-surface-secondary p-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-text-primary">Plan</label>
              <select
                value={filters.plan}
                onChange={(e) => updateFilters({ plan: e.target.value as FirmPlan | '' })}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {planOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end md:col-span-2">
              <Button variant="outline" size="sm" onClick={clearAllFilters}>
                <X className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Firm List Table */}
      <div className="relative flex-1 overflow-auto">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="text-center">
              <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-text-secondary" />
              <p className="text-text-secondary">Loading firms...</p>
            </div>
          </div>
        )}
        <div className="p-6">
          <div className="rounded-md border border-stroke-primary">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">ID</TableHead>
                  <TableHead>Firm Name</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Updated At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {firms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <Building2 className="mb-2 h-8 w-8 text-text-secondary" />
                        <p className="text-sm text-text-secondary">No firms found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  firms.map((firm) => (
                    <TableRow key={firm.id} className="cursor-pointer" onClick={() => handleFirmClick(firm)}>
                      <TableCell className="font-medium">{firm.id}</TableCell>
                      <TableCell className="font-medium">{firm.name}</TableCell>
                      <TableCell>
                        <Badge variant={getPlanBadgeVariant(firm.plan)}>{firm.plan}</Badge>
                      </TableCell>
                      <TableCell className="text-text-secondary">
                        {format(new Date(firm.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell className="text-text-secondary">
                        {format(new Date(firm.updated_at), 'MMM dd, yyyy')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {firms.length === 0 && !isLoading && (
            <div className="mt-4 text-center">
              <p className="text-text-secondary">
                {filters.name || filters.plan ? 'Try adjusting your search criteria' : 'No firms available'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-stroke-primary bg-background p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span>
                Showing {startIndex}-{endIndex} of {total} firms
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">
                Page {currentPage} of {totalPages}
              </span>
            </div>

            <div className="flex items-center gap-1">
              {/* First page button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={currentPage === 1}
                className="hidden sm:flex"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>

              {/* Previous button */}
              <Button variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
                <ChevronLeft className="h-4 w-4" />
                <span className="ml-1 hidden sm:inline">Previous</span>
              </Button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {paginationPages.map((page, index) => {
                  if (page === 'ellipsis') {
                    return (
                      <span key={`ellipsis-${index}`} className="px-2 text-text-secondary">
                        ...
                      </span>
                    );
                  }
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(page)}
                      className="min-w-[40px]"
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>

              {/* Next button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <span className="mr-1 hidden sm:inline">Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Last page button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={currentPage === totalPages}
                className="hidden sm:flex"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Firm Detail Drawer */}
      <FirmDetailDrawer isOpen={!!selectedFirm} onClose={handleCloseDrawer} firm={selectedFirm} />
    </div>
  );
}
